import { useEffect, useState } from 'react'
import {
  Button,
  Flex,
  Input,
  Modal,
  Paragraph,
  Popconfirm,
  Switch,
  Table,
  Text,
  message,
  tableActionsCell,
} from '@nula/core-ui'
import * as Sentry from '@sentry/react'
import { useNavigate } from '@tanstack/react-router'
import { ApiError } from '@/lib/http/client'
import type { AllowedDomain, WorkspaceInfo } from '@/lib/workspace/types/types'
import { useWorkspaceStore } from '@/stores/use-workspace-store'
import { canDeleteWorkspace } from '../../../utils/permissions'
import { updateWorkspace, deleteWorkspace, listAllowedDomains, addAllowedDomain, removeAllowedDomain } from '@/lib/workspace/api/api'
import { isFreeEmailDomain } from '../../../utils/free-email-domains'
import styles from './general-tab.module.css'

interface GeneralTabProps {
  workspace: WorkspaceInfo
}

export function GeneralTab({ workspace }: GeneralTabProps) {
  const canDelete = canDeleteWorkspace(workspace.role)
  const refresh = useWorkspaceStore((s) => s.refreshFromServer)
  const switchToWorkspace = useWorkspaceStore((s) => s.switchToWorkspace)
  const workspaces = useWorkspaceStore((s) => s.workspaces)
  const navigate = useNavigate()

  // Workspace name
  const [nameValue, setNameValue] = useState(workspace.name)
  const [saving, setSaving] = useState(false)
  const nameDirty = nameValue.trim() !== workspace.name && nameValue.trim().length > 0

  // Delete confirmation
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmName, setConfirmName] = useState('')
  const [deleting, setDeleting] = useState(false)

  // Allowed domains
  const [domains, setDomains] = useState<AllowedDomain[]>([])
  const [newDomain, setNewDomain] = useState('')
  const [adding, setAdding] = useState(false)
  const [domainReloadKey, setDomainReloadKey] = useState(0)

  // Auto-join toggle — when true, new signups whose email domain matches one
  // of the allowed-domain rows are auto-added as 'member' on signup.
  const [autoJoinPending, setAutoJoinPending] = useState(false)

  const handleAutoJoinToggle = async (enabled: boolean) => {
    setAutoJoinPending(true)
    try {
      await updateWorkspace(workspace.id, { autoJoinEnabled: enabled })
      await refresh()
    } catch (err) {
      console.error('[GeneralTab] auto-join toggle failed:', err)
      Sentry.captureException(err)
      message.error('Could not update auto-join setting.')
    } finally {
      setAutoJoinPending(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    listAllowedDomains(workspace.id)
      .then((list) => { if (!cancelled) setDomains(list) })
      .catch((err: unknown) => {
        if (cancelled) return
        console.error('[GeneralTab] listAllowedDomains failed:', err)
        Sentry.captureException(err)
        message.error('Failed to load allowed domains.')
      })
    return () => { cancelled = true }
  }, [workspace.id, domainReloadKey])

  const handleNameSave = async () => {
    const trimmed = nameValue.trim()
    if (!trimmed || trimmed === workspace.name) return
    setSaving(true)
    try {
      await updateWorkspace(workspace.id, { name: trimmed })
      await refresh()
    } catch (err) {
      console.error('[GeneralTab] update failed:', err)
      Sentry.captureException(err)
      message.error('Could not update workspace name.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await deleteWorkspace(workspace.id)
      const personal = workspaces.find((w) => w.type === 'personal')
      if (personal) {
        await switchToWorkspace(personal.id, (path) => navigate({ to: path }))
      }
      await refresh()
    } catch (err: unknown) {
      console.error('[GeneralTab] delete failed:', err)
      Sentry.captureException(err)
      message.error(err instanceof ApiError ? err.message : 'Failed to delete workspace.')
    } finally {
      setDeleting(false)
    }
  }

  const handleAddDomain = async () => {
    const trimmed = newDomain.trim().toLowerCase()
    if (!trimmed.includes('.')) {
      message.error('Domain must include a dot (e.g. "example.com").')
      return
    }
    const sanitized = trimmed.replace(/^\.+|\.+$/g, '')
    if (sanitized !== trimmed) {
      message.error('Domain cannot start or end with a dot.')
      return
    }
    if (/\s/.test(trimmed)) {
      message.error('Domain cannot contain whitespace.')
      return
    }
    if (isFreeEmailDomain(trimmed)) {
      message.error("Free-email domains (gmail.com, etc.) can't be used for auto-join. Use your organization's domain.")
      return
    }
    setAdding(true)
    try {
      await addAllowedDomain(workspace.id, trimmed)
      setNewDomain('')
      setDomainReloadKey((k) => k + 1)
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 422) {
        message.error('Domain is rejected (free-email provider or malformed).')
      } else {
        console.error('[GeneralTab] addAllowedDomain failed:', err)
        Sentry.captureException(err)
        message.error('Could not add domain.')
      }
    } finally {
      setAdding(false)
    }
  }

  const handleRemoveDomain = async (domain: string) => {
    try {
      await removeAllowedDomain(workspace.id, domain)
      setDomainReloadKey((k) => k + 1)
    } catch (err) {
      console.error('[GeneralTab] removeAllowedDomain failed:', err)
      Sentry.captureException(err)
      message.error('Could not remove domain.')
    }
  }

  const domainColumns = [
    { title: 'Domain', dataIndex: 'domain', key: 'domain' },
    {
      title: '',
      key: 'actions',
      render: (_: unknown, d: AllowedDomain) => (
        <div className={tableActionsCell}>
          <Popconfirm
            title="Remove domain"
            description={`Remove ${d.domain}?`}
            okText="Remove"
            okDanger
            onConfirm={() => handleRemoveDomain(d.domain)}
          >
            <Button variant="text">Remove</Button>
          </Popconfirm>
        </div>
      ),
    },
  ]

  return (
    <Flex vertical gap="lg">
      {/* ── Tab header ───────────────────────────────────────────────────── */}
      <Flex vertical gap="xs">
        <Text size="md" className={styles.tabTitle}>General</Text>
        <Text size="sm" type="secondary">Workspace name, access rules, and danger zone.</Text>
      </Flex>

      {/* ── Workspace name ───────────────────────────────────────────────── */}
      <Flex vertical className={styles.section}>
        <Flex vertical gap="sm">
          <Flex vertical gap="xs">
            <Text size="sm" className={styles.fieldLabel}>Name</Text>
            <Input
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && nameDirty && !saving) void handleNameSave() }}
              size="md"
            />
          </Flex>
          <Flex className={styles.saveRow}>
            <Button variant="primary" size="md" pressEffect="glow" onClick={handleNameSave} disabled={!nameDirty || saving} loading={saving}>
              Save Changes
            </Button>
          </Flex>
        </Flex>
      </Flex>

      {/* ── Allowed email domains ─────────────────────────────────────────── */}
      <Flex vertical gap="sm" className={styles.section}>
        <Text className={styles.sectionLabel}>Allowed email domains</Text>
        <Text size="sm" type="secondary">
          Only users on these domains can join via invite. Leave empty to allow any verified email.
        </Text>

        {domains.length > 0 && (
          <Table
            rowKey="domain"
            dataSource={domains as unknown as Record<string, unknown>[]}
            columns={domainColumns as Parameters<typeof Table>[0]['columns']}
            pagination={false}
          />
        )}

        <Flex gap="sm" className={styles.addRow}>
          <Input
            className={styles.addInput}
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newDomain.trim() && !adding) handleAddDomain()
            }}
            label="Domain"
            aria-label="Add allowed domain"
          />
          <Button onClick={handleAddDomain} disabled={!newDomain.trim() || adding} loading={adding}>
            Add
          </Button>
        </Flex>

        {/* Auto-join toggle. Sits under the domains list so the read order
            is: "here are the allowed domains" → "add one" → "and here's how
            to make signups from those domains join automatically". */}
        <Flex align="center" gap="sm" className={styles.autoJoinRow}>
          <Switch
            checked={workspace.autoJoinEnabled}
            onChange={(v) => void handleAutoJoinToggle(v)}
            disabled={autoJoinPending}
            aria-label="Auto-join new signups matching an allowed domain"
          />
          <Flex vertical gap="xs">
            <Text size="sm">Auto-join new signups from these domains</Text>
            <Text size="xs" type="secondary">
              When enabled, anyone who signs up with an email on the list above is added as a member automatically — no invite needed.
            </Text>
          </Flex>
        </Flex>
      </Flex>

      {/* ── Danger zone (owner only) ─────────────────────────────────────── */}
      {canDelete && (
        <Flex vertical gap="sm" className={styles.dangerSection}>
          <Text className={styles.dangerLabel}>Danger zone</Text>
          <Paragraph type="secondary">
            Deleting this workspace removes all documents, recordings, and member access. This cannot be undone.
          </Paragraph>
          <Button danger onClick={() => setConfirmOpen(true)}>
            Delete this workspace
          </Button>
        </Flex>
      )}

      <Modal
        open={confirmOpen}
        onClose={() => { setConfirmOpen(false); setConfirmName('') }}
        title={`Delete ${workspace.name}?`}
        footer={
          <>
            <Button variant="text" onClick={() => { setConfirmOpen(false); setConfirmName('') }}>
              Cancel
            </Button>
            <Button
              danger
              disabled={confirmName !== workspace.name || deleting}
              loading={deleting}
              onClick={handleDelete}
            >
              Delete workspace
            </Button>
          </>
        }
      >
        <Flex vertical gap="md">
          <Paragraph>
            Type <Text strong>{workspace.name}</Text> below to confirm.
          </Paragraph>
          <Input
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            label={workspace.name}
            autoFocus
          />
        </Flex>
      </Modal>
    </Flex>
  )
}
